// ─── Consent to AI (Sona) — the approved document text ───────────────────────
//
// One constant holds the words stamped into the rendered PDF as the member's
// consent record: the text Legal approved on 2026-09-11, minus the one
// sentence that titled Sona with a role word, which the Sona naming standard
// (ADR-144 Rule 1) later banned — removed on 2026-09-25 with the copy owners'
// agreement, which also leaves that item reading exactly as the migration
// screen does. Any change to this text is a new template version, never an
// in-place edit.

export interface SonaConsentDocumentItem {
  /** Bold lead-in of the item, ending in a full stop. */
  readonly title: string;
  /** The item body, one or two sentences. */
  readonly body: string;
}

export interface SonaConsentDocument {
  /** Document title as printed on the PDF and filed in the chart. */
  readonly title: string;
  /** The heading the member reads first on the screen and the page. */
  readonly heading: string;
  /** One-line intro under the heading. */
  readonly intro: string;
  /** The four things to know, in display order. */
  readonly items: readonly [
    SonaConsentDocumentItem,
    SonaConsentDocumentItem,
    SonaConsentDocumentItem,
    SonaConsentDocumentItem,
  ];
  /** Crisis line printed after the items. */
  readonly crisis: string;
  /** Statement above the stamped name and date. */
  readonly signatureStatement: string;
}

export const SONA_CONSENT_DOCUMENT: SonaConsentDocument = {
  title: 'Consent to AI: Talking with Sona',
  heading: 'Sona gets to know you through conversation',
  intro: 'A few things to know first.',
  items: [
    {
      title: 'Sona is AI, not a clinician.',
      body: 'Sona can’t diagnose or treat, and isn’t a substitute for clinical care.',
    },
    {
      title: 'It shapes your plan.',
      body: 'Sona gets to know you through conversation to support your intake and personalize your plan. Everything it gathers goes to your care team, who regularly review what you share.',
    },
    {
      title: 'You’re in control.',
      body: 'Using Sona is voluntary. Pause, switch to text, or turn Sona off at any time in Settings. Your program and your care team stay the same either way.',
    },
    {
      title: 'How your conversations are stored.',
      body: 'Your conversations are stored, may be reviewed to improve Sona, and are never sold or used for advertising.',
    },
  ],
  crisis: 'In a crisis, call or text 988.',
  signatureStatement:
    'My name below reflects my signature and the time and date on which I accepted these terms.',
};
